/**
 * FAQ page: live question search, topic navigation, and active-topic tracking.
 */
class PjFaqPage extends HTMLElement {
  connectedCallback() {
    this.searchInput = this.querySelector('[data-pj-faq-search]');
    this.empty = this.querySelector('[data-pj-faq-empty]');
    this.status = this.querySelector('[data-pj-faq-status]');
    this.items = [...this.querySelectorAll('[data-pj-faq-item]')];
    this.groups = [...this.querySelectorAll('[data-pj-faq-group]')];
    this.topicButtons = [...this.querySelectorAll('[data-pj-faq-topic]')];
    this.resultsCount = this.items.length;
    this.#activeId = this.topicButtons[0]?.dataset.pjFaqTopic || '';
    this.#scrolling = false;

    this.searchInput?.addEventListener('input', () => this.#filter());
    this.searchInput?.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        this.searchInput.value = '';
        this.#filter();
      }
    });

    this.topicButtons.forEach((button) => {
      button.addEventListener('click', (event) => this.#onTopicClick(event, button));
    });

    this.#observer = new IntersectionObserver((entries) => this.#onIntersect(entries), {
      root: null,
      rootMargin: '-30% 0px -55% 0px',
      threshold: 0,
    });
    this.groups.forEach((group) => this.#observer.observe(group));
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
  }

  /** @type {IntersectionObserver | undefined} */
  #observer;

  /** @type {string} */
  #activeId = '';

  /** @type {boolean} */
  #scrolling = false;

  /**
   * @param {MouseEvent} event
   * @param {HTMLElement} button
   */
  #onTopicClick(event, button) {
    event.preventDefault();
    const id = button.dataset.pjFaqTopic;
    const target = id ? this.querySelector(`#${CSS.escape(id)}`) : null;
    if (!(target instanceof HTMLElement)) return;

    this.#setActive(id);
    this.#scrolling = true;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
    window.setTimeout(() => {
      this.#scrolling = false;
    }, 800);
  }

  /**
   * @param {IntersectionObserverEntry[]} entries
   */
  #onIntersect(entries) {
    if (this.#scrolling || this.searchInput?.value.trim()) return;

    const visible = entries
      .filter((entry) => entry.isIntersecting)
      .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);

    const id = visible[0]?.target?.id;
    if (id) this.#setActive(id);
  }

  /**
   * @param {string} id
   */
  #setActive(id) {
    if (!id || id === this.#activeId) return;
    this.#activeId = id;

    this.topicButtons.forEach((button) => {
      const isActive = button.dataset.pjFaqTopic === id;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-current', isActive ? 'true' : 'false');
    });
  }

  #filter() {
    const query = this.searchInput?.value.trim().toLowerCase() || '';
    let visibleCount = 0;

    this.items.forEach((item) => {
      const haystack = item.dataset.pjFaqSearch || '';
      const match = query === '' || haystack.includes(query);
      item.hidden = !match;
      if (match) visibleCount += 1;
    });

    this.groups.forEach((group) => {
      const hasVisible = [...group.querySelectorAll('[data-pj-faq-item]')].some((item) => !item.hidden);
      group.hidden = !hasVisible;
      const button = this.topicButtons.find((topic) => topic.dataset.pjFaqTopic === group.id);
      if (button) button.hidden = !hasVisible;
    });

    if (this.empty) this.empty.hidden = visibleCount > 0;
    this.resultsCount = visibleCount;

    if (this.status) {
      this.status.textContent = query ? `${visibleCount}` : '';
    }

    if (!query) {
      const firstVisible = this.groups.find((group) => !group.hidden);
      if (firstVisible) this.#setActive(firstVisible.id);
    }
  }
}

if (!customElements.get('pj-faq-page')) {
  customElements.define('pj-faq-page', PjFaqPage);
}
